import {Mapper} from '@s-ui/domain'

import {DrivenWheelsSpecificationVehiclesValueObject} from '../../vehicles/ValueObjects/DrivenWheelsSpecificationVehiclesValueObject.js'
import {OfferPriceValueObject} from '../ValueObjects/OfferPriceValueObject.js'
import {PriceDropValueObject} from '../ValueObjects/PriceDropValueObject.js'

/**
 * @implements Mapper
 * Use the raw response from the http repository to create an Ad Entity.
 */
export class FromAdDetailsRawDataToAdEntityMapper extends Mapper {
  constructor({adEntityFactory, config, getSellerRequestMapper}) {
    super()

    const {FUEL_TYPES} = config.get('advertising')

    this._config = config
    const {AD_STATUS, bodyTypes = [], colors = []} = this._config.get('search')
    this._AD_STATUS = AD_STATUS
    this._adEntityFactory = adEntityFactory
    this._appName = this._config.get('APP_NAME')
    this._bodyTypes = bodyTypes
    this._bodyTypesTranslations = this._config.get('tracking').BODY_TYPES_TRANSLATIONS
    this._colors = colors
    this._culture = this._config.get('DEFAULT_CULTURE')
    this._fuelTypes = FUEL_TYPES
    this._getSellerRequestMapper = getSellerRequestMapper
    this._provinces = this._config.get('provinces')
    this._targetBuyerIds = this._config.get('subscription').TARGET_BUYER_IDS
    this._transmissionTypes = config.get('search').transmissionTypes || []
  }

  _getColorName = colorId => {
    const colorName = this._colors.find(color => String(color.id) === String(colorId))
    return colorName && colorName.label
  }

  _getFuelType = ({fuelTypeId}) => this._fuelTypes[fuelTypeId]?.[this._culture] || ''

  _getProvinceName = provinceId => {
    if (!provinceId) return
    const {label: provinceName} = this._provinces.find(province => String(province.id) === String(provinceId))
    return provinceName
  }

  _getTransmissionName = ({transmissionTypeId}) => {
    const [transmissionType] = this._transmissionTypes.filter(({id}) => Number(id) === Number(transmissionTypeId))
    return transmissionType ? transmissionType.label : undefined
  }

  _getIncludesTaxes = ({price, subscriptionFeatures}) => {
    if (typeof price.hasTaxes !== 'undefined') return price.hasTaxes
    if (typeof subscriptionFeatures.taxesIncluded !== 'undefined') return subscriptionFeatures.taxesIncluded
  }

  _getAvailableColors = (colors = []) => {
    return colors.map(({colorId}) => {
      return this._getColorName(colorId)
    })
  }

  _getSubscriptionMonthlyFee = ({subscriptionPriceOptions = []}) => {
    return subscriptionPriceOptions.reduce((minimumMonthlyFee, {monthlyPrice}) => {
      if (minimumMonthlyFee === 0) return monthlyPrice
      return monthlyPrice < minimumMonthlyFee ? monthlyPrice : minimumMonthlyFee
    }, 0)
  }

  _getRentingProperties = ({subscriptionFeatures = {}}) => {
    const availableColors = this._getAvailableColors(subscriptionFeatures.colors)
    const subscriptionCommitmentOptions = subscriptionFeatures.commitments

    const subscriptionBusinessPriceOptions = subscriptionCommitmentOptions.filter(
      ({targetBuyer}) => targetBuyer === this._targetBuyerIds.BUSINESS
    )
    const subscriptionPrivatePriceOptions = subscriptionCommitmentOptions.filter(
      ({targetBuyer}) => targetBuyer !== this._targetBuyerIds.BUSINESS
    )
    const hasPrivateSubscriptionOptions = subscriptionPrivatePriceOptions.length > 0
    const hasBusinessSubscriptionOptions = subscriptionBusinessPriceOptions.length > 0
    const hasBothSubscriptionOptions = hasPrivateSubscriptionOptions && hasBusinessSubscriptionOptions

    const subscriptionBusinessAdInfo = {
      targetBuyer: this._targetBuyerIds.BUSINESS,
      monthlySubscriptionFee: this._getSubscriptionMonthlyFee({
        subscriptionPriceOptions: subscriptionBusinessPriceOptions
      }),
      subscriptionPriceOptions: subscriptionBusinessPriceOptions
    }

    const subscriptionPriceOptions = hasPrivateSubscriptionOptions
      ? subscriptionPrivatePriceOptions
      : subscriptionBusinessPriceOptions

    return {
      availableColors,
      monthlySubscriptionFee: this._getSubscriptionMonthlyFee({subscriptionPriceOptions}),
      subscriptionBusinessAdInfo: hasBothSubscriptionOptions ? subscriptionBusinessAdInfo : undefined,
      subscriptionPriceOptions: !hasPrivateSubscriptionOptions
        ? subscriptionBusinessPriceOptions
        : subscriptionPrivatePriceOptions,
      subscriptionVehicleState: subscriptionFeatures.vehicleState,
      targetBuyer: !hasPrivateSubscriptionOptions ? this._targetBuyerIds.BUSINESS : this._targetBuyerIds.PRIVATES
    }
  }

  _getPriceDrop = ({price}) => {
    const priceDropVO = PriceDropValueObject.createFromApi({price})
    return priceDropVO?.value ?? null
  }

  _getTraction = ({drivenWheelsId}) => {
    const drivenWheelsVo = DrivenWheelsSpecificationVehiclesValueObject.createFromId({id: drivenWheelsId})
    return drivenWheelsVo?.name ?? undefined
  }

  map = async rawData => {
    const {ad} = rawData
    const {price = {}, subscriptionFeatures = {}, vehicle = {}, offerTypeId} = ad
    const {specs: adVehicleSpecs = {}, warranty = {}} = vehicle
    const {privateSeller = {}, professionalSeller = {}} = rawData

    const sellerRequestVO = this._getSellerRequestMapper.map({
      contractId: professionalSeller.dealerIdentity?.id || professionalSeller.id,
      userId: ad.privateSeller?.userId || privateSeller.userId
    })

    const {isDealer} = sellerRequestVO.toJSON()

    const phone = isDealer ? professionalSeller?.contact?.phone : privateSeller.phone

    const [firstPhoto] = ad.photos

    const hasSubscriptionFeatures = Object.entries(subscriptionFeatures).length !== 0

    const hasStock = Boolean(ad.hasStock) || (hasSubscriptionFeatures && Boolean(subscriptionFeatures.hasStock))
    const hasPriceIndicator = Boolean(price.indicator)

    const hasPriceOffer = Boolean(price.offer)
    const hasHidePhone = Boolean(
      privateSeller?.hidePhone !== undefined || professionalSeller?.contact?.hidePhone !== undefined
    )
    const priceDrop = this._getPriceDrop({price})

    const traction = this._getTraction({drivenWheelsId: adVehicleSpecs.drivenWheelsId})

    const offer =
      hasPriceOffer &&
      OfferPriceValueObject.createFromApi({
        offerPrice: price.offer?.offerPrice,
        manufacturerPrice: price.offer?.manufacturerPrice || price.price
      })

    return this._adEntityFactory({
      ...(hasSubscriptionFeatures && this._getRentingProperties({subscriptionFeatures})),
      ...(hasPriceIndicator && {
        priceAverageIndicator: price.indicator.average
      }),
      bodyTypeId: adVehicleSpecs.bodyTypeId,
      color: this._getColorName(vehicle.colorId) || vehicle.color,
      creationDate: ad.creationDate,
      description: ad.description,
      environmentalLabel: adVehicleSpecs.environmentalLabel,
      externalAdId: ad.externalAdId,
      financedPrice: price.financedPrice,
      fuelType: this._getFuelType({fuelTypeId: adVehicleSpecs.fuelTypeId}),
      fuelTypeId: adVehicleSpecs.fuelTypeId,
      hasOfficialWarranty: warranty.isOfficial,
      hasStock,
      hasWarranty: Boolean(warranty),
      ...(hasHidePhone && {
        hidePhone: Boolean(privateSeller?.hidePhone || professionalSeller?.contact?.hidePhone)
      }),
      id: ad.id,
      img: firstPhoto,
      includesTaxes: this._getIncludesTaxes({price, subscriptionFeatures}),
      isCertified: vehicle.isCertified,
      isFinanced: Boolean(price.financedPrice),
      isProfessional: isDealer,
      km: vehicle.km,
      location: ad.location,
      make: adVehicleSpecs.make,
      makeId: adVehicleSpecs.makeId,
      model: adVehicleSpecs.model,
      modelId: adVehicleSpecs.modelId,
      monthlyFee: price.monthlyFee,
      ...(hasPriceOffer && {
        offer: {
          offerPrice: offer?.offerPrice,
          manufacturerPrice: offer?.manufacturerPrice,
          discountPercentage: offer?.getDiscountPercentageLiteral()
        }
      }),
      offerType: {
        id: offerTypeId
      },
      phone,
      photos: ad.photos,
      plateRegistrationDate: vehicle.plateRegistrationDate,
      price: price.price,
      priceDrop,
      province: this._getProvinceName(ad.location?.provinceIds[0] || ad.provinceId),
      provinceId: ad.provinceId,
      publicationDate: ad.publicationDate,
      ...(hasPriceIndicator && {priceRankIndicator: price.indicator.rank}),
      taxTypeId: price.taxTypeId,
      title: ad.title,
      transmissionType: this._getTransmissionName({
        transmissionTypeId: adVehicleSpecs.transmissionTypeId
      }),
      traction,
      transmissionTypeId: adVehicleSpecs.transmissionTypeId,
      url: ad.url,
      version: adVehicleSpecs.version,
      versionId: adVehicleSpecs.versionId,
      videos: ad.videos,
      warrantyId: warranty.typeId,
      warrantyMonths: warranty.months,
      warrantyText: warranty.text,
      year: vehicle.year
    })
  }
}
